import React, { useState, useEffect, useContext } from 'react';
import styles from './ComCreateCampaigns.module.css';
import { FaCheckCircle, FaRegCircle } from 'react-icons/fa'; // React Icons
import CampaignDetails from '../../Components/Communications/Campaigns/CampaignDetails';
import MessageTemplates from '../../Components/Communications/Campaigns/MessageTemplates';
import CampaignAudience from '../../Components/Communications/Campaigns/CampaignAudience';
import CampaignLaunch from '../../Components/Communications/Campaigns/CampaignLaunch';
import CampaignPerformance from '../../Components/Communications/Campaigns/CampaignPerformance';
import '../../Components/Communications/Campaigns/shared/campaignConsole.css';
import { deriveSendState } from '../../Components/Communications/Campaigns/shared/campaignStatus';
import AuthContext from '../../store/auth-context';
import ActiveStoreContext from '../../store/ActiveStoreContext';
import { Link, useNavigate, useParams } from 'react-router';
import { GoArrowLeft, GoArrowRight } from "react-icons/go";
import { BASE_URL_API } from '../../References/URLs';
import CampaignsPaymentStatus from './CampaignsPaymentStatus';
import { toast } from 'react-toastify';

// Ordered list of the wizard's real steps, used to compute progress and
// sidebar highlighting. Scheduling is no longer its own step — real user
// testing showed a standalone step forced an extra click, and worse, left
// no way back to relaunch/reschedule after cancelling. It's folded into
// the Launch step itself now (see CampaignLaunch.js). 'add-money' is still
// reachable by URL but intentionally excluded from the visible step
// tracker (see docs).
const WIZARD_STEPS = ['details', 'templates', 'audience', 'launch', 'performance'];

const ComCreateCampaigns = () => {
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = (() => { try { return (asCtx?.activeStore && asCtx.activeStore.trim() !== "") ? JSON.parse(asCtx.activeStore) : null; } catch { return null; } })();
    const { campaignIdentifier, currentStep } = useParams()
    const nav = useNavigate()
    const [campaignData, setCampaignData] = useState({
        name: '',
        messageTemplate: '',
        audience: '',
        deliveryMessageSource: '',
        schedule: '',
        status: 'draft',
    });

    const stepIndex = WIZARD_STEPS.indexOf(currentStep)
    const progress = stepIndex >= 0 ? ((stepIndex + 1) / WIZARD_STEPS.length) * 100 : 0
    const prevStepName = stepIndex > 0 ? WIZARD_STEPS[stepIndex - 1] : null
    const nextStepName = stepIndex >= 0 && stepIndex < WIZARD_STEPS.length - 1 ? WIZARD_STEPS[stepIndex + 1] : null
    const goToStep = (step) => nav(`/communications/campaign/${campaignIdentifier}/${step}`)

    // Mirrors CampaignLaunch.js's own isAvailable — its fixed bottom Launch
    // Now bar (mobile) only renders in that state. Whenever it's scheduled
    // or firing/sent, that bar disappears, so this wizard-level Prev/Next
    // bar needs to take over rather than leaving the Launch step with no
    // bottom nav at all on mobile.
    const { isScheduledNotFired, isFiring } = deriveSendState({
        holdStatus: campaignData?.holdStatus,
        scheduledAt: campaignData?.scheduledAt,
        broadcastJobId: campaignData?.broadcastJobId,
    })
    const isLaunchBarShowing = currentStep === 'launch' && !isScheduledNotFired && !isFiring

    // This screen has its own fixed elements at both the top (header) and,
    // on most steps, the bottom (step nav / Launch Now) — neither corner the
    // global hamburger FAB parks in is actually free here, so it's hidden
    // entirely for the whole wizard rather than just repositioned.
    useEffect(() => {
        window.dispatchEvent(new CustomEvent('layout:hideFab', { detail: { active: true } }))
        return () => {
            window.dispatchEvent(new CustomEvent('layout:hideFab', { detail: { active: false } }))
        }
    }, [])

    // Step children (e.g. TemplatePreview) dispatch this while they have
    // their own full-screen overlay open with its own fixed bottom action
    // bar — otherwise this bar and the overlay's bar are both
    // position:fixed at the same z-index, and fixed elements escape their
    // DOM ancestors' stacking context, so this one (later in the DOM) would
    // paint on top of and intercept touches meant for the overlay's own
    // buttons underneath it.
    const [isMobileNavHidden, setIsMobileNavHidden] = useState(false)
    useEffect(() => {
        const onHideMobileNavChange = (e) => setIsMobileNavHidden(!!e.detail?.active)
        window.addEventListener('campaignWizard:hideMobileNav', onHideMobileNavChange)
        return () => window.removeEventListener('campaignWizard:hideMobileNav', onHideMobileNavChange)
    }, [])

    // Only the setup steps (name/template/audience) plus Launch have a
    // meaningful "done" concept to reflect as a checkmark — Performance is
    // the results step, not something you "complete" the same way.
    const isStepComplete = (step) => {
        if (step === 'details') return !!campaignData?.campaignName
        if (step === 'templates') return !!campaignData?.templateName
        // The real getCampaignDetails response carries the selected audience
        // as audienceIdentifier (audienceName can be null even when an
        // audience IS selected) — there is no field literally called
        // "audience" on this object, that was a wrong guess based on the
        // wizard's local campaignData initializer shape, not the real API.
        if (step === 'audience') return !!campaignData?.audienceIdentifier
        // Same send-state derivation as the Launch step's own isAvailable —
        // scheduled-not-fired or actively sending/sent both mean a launch
        // action was actually taken. RELEASED (cancelled) deliberately
        // stays unchecked: isAvailable goes back to true there so the user
        // can relaunch, and the sidebar should reflect that it's not done.
        if (step === 'launch') return isScheduledNotFired || isFiring
        return false
    }

    const getCampaignDetails = async () => {
        const response = await fetch(BASE_URL_API +"getCampaignDetails",
        {
            method : "POST",
            body : JSON.stringify({
                token : authCtx.token,
                campaignIdentifier : campaignIdentifier
            })
        });
    
        if(!response.ok) {
            toast.error(`Couldn't load this campaign. Please try again.`)
            return
        }

        const respo = await response.json()
        if(respo.status==="success") {
            setCampaignData(respo.response)
        } else {
            // Was previously checking a different sentinel string
            // ("token Expired, please login again!") than every other file
            // in this codebase ("tokenExpired"), so this logout branch was
            // effectively dead — fixed to match the established convention.
            if(respo.message==="tokenExpired") {
                authCtx.logout()
            } else {
                toast.error(respo?.message || `Couldn't load this campaign. Please try again.`)
            }
        }
    }

    useEffect(() => {
        getCampaignDetails()
    }, [campaignIdentifier])



    if (!activeStore) {
        return (
            <div className={styles.container} >
                Please select a store!
            </div>
        )
    }

    return (
        <div className={`${styles.container} ccScope`}>
            {/* Left Sidebar (desktop) / Header (mobile) */}
            <div className={styles.sidebar}>
                <Link to={`/communications/all-campaigns`} className={styles.goToAllCampaignsLink}>
                    <div className={styles.goToAllCampaigns}>
                    <GoArrowLeft size={20} /> <span>Go To All Campaigns</span>
                    </div>
                </Link>
                <div className={styles.pillsScroller}>
                    <Link to={`/communications/campaign/${campaignIdentifier}/details`}
                        className={`${styles.sidebarItem} ${currentStep === 'details' ? styles.active : ''}`}
                    >
                        {isStepComplete('details') ? <FaCheckCircle className={styles.completeIcon} /> : <FaRegCircle />} Campaign
                    </Link>
                    <Link to={`/communications/campaign/${campaignIdentifier}/templates`}
                        className={`${styles.sidebarItem} ${currentStep === 'templates' ? styles.active : ''}`}
                    >
                        {isStepComplete('templates') ? <FaCheckCircle className={styles.completeIcon} /> : <FaRegCircle />} Message Template
                    </Link>
                    <Link to={`/communications/campaign/${campaignIdentifier}/audience`}
                        className={`${styles.sidebarItem} ${currentStep === 'audience' ? styles.active : ''}`}
                    >
                        {isStepComplete('audience') ? <FaCheckCircle className={styles.completeIcon} /> : <FaRegCircle />} Audience
                    </Link>
                    <Link to={`/communications/campaign/${campaignIdentifier}/launch`}
                        className={`${styles.sidebarItem} ${currentStep === 'launch' ? styles.active : ''}`}
                    >
                        {isStepComplete('launch') ? <FaCheckCircle className={styles.completeIcon} /> : <FaRegCircle />} Launch
                    </Link>
                    <Link to={`/communications/campaign/${campaignIdentifier}/performance`}
                        className={`${styles.sidebarItem} ${currentStep === 'performance' ? styles.active : ''}`}
                    >
                        <FaRegCircle /> Performance
                    </Link>
                </div>
            </div>

            {/* Main Content */}
            <div className={`${styles.content} ${!isLaunchBarShowing ? styles.contentWithMobileNav : ''}`}>
                {currentStep === 'details' && <CampaignDetails data={campaignData} setData={setCampaignData} />}
                {currentStep === 'templates' && <MessageTemplates data={campaignData} setData={setCampaignData} getCampaignDetails={getCampaignDetails} />}
                {currentStep === 'audience' && <CampaignAudience data={campaignData} setData={setCampaignData} getCampaignDetails={getCampaignDetails} />}
                {currentStep === 'launch' && <CampaignLaunch data={campaignData} setData={setCampaignData} getCampaignDetails={getCampaignDetails} />}
                {currentStep === 'performance' && <CampaignPerformance data={campaignData} setData={setCampaignData} />}

                {currentStep === 'add-money' && <CampaignsPaymentStatus data={campaignData} setData={setCampaignData} />}
            </div>

            {/* Footer and Progress (desktop only — mobile shows this in the
                header via `order` in the CSS, see the mobile media query) */}
            <div className={styles.progressContainer}>
                <div className={styles.progressBarWrapper}>
                    <div className={styles.progressBar} style={{ width: `${progress}%` }}></div>
                </div>
            </div>

            {/* Mobile-only prev/next step nav. Skipped only while the Launch
                step's own full-width fixed-bottom Launch Now button is
                actually showing (isLaunchBarShowing) — a second bottom bar
                there would compete for the same, most-important tap target
                on the whole page. Once the campaign is scheduled or firing/
                sent, that bar disappears (see CampaignLaunch.js isAvailable),
                so this nav takes back over rather than leaving the step with
                no way to move between steps. Also hidden whenever a step's
                own overlay (e.g. TemplatePreview) has its own fixed bottom
                bar open — see isMobileNavHidden. */}
            {!isLaunchBarShowing && !isMobileNavHidden && (
                <div className={styles.mobileStepNav}>
                    <button
                        type="button"
                        className={styles.mobileStepNavBtn}
                        onClick={() => prevStepName && goToStep(prevStepName)}
                        disabled={!prevStepName}
                    >
                        <GoArrowLeft size={16} /> Prev
                    </button>
                    <button
                        type="button"
                        className={`${styles.mobileStepNavBtn} ${styles.mobileStepNavBtnPrimary}`}
                        onClick={() => nextStepName && goToStep(nextStepName)}
                        disabled={!nextStepName}
                    >
                        Next <GoArrowRight size={16} />
                    </button>
                </div>
            )}

        </div>
    );
};

export default ComCreateCampaigns;
